import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { ArrowLeftIcon, UserIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { userService } from '../services/userService.ts';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import api from '../services/api.ts';
import FormPage from '../components/ui/FormPage.tsx';

const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user' as 'admin' | 'user',
    avatar: [] as FileAttachment[]
  });

  const createUserMutation = useMutation(
    async (userData: { name: string; email: string; password: string; role: 'admin' | 'user'; avatar?: FileAttachment[] }) => {
      let avatarUrl = '';
      
      // Primeiro, fazer upload do avatar se existir
      const newAvatarFile = userData.avatar?.find(file => file.file instanceof File);
      if (newAvatarFile && newAvatarFile.file) {
        const formDataUpload = new FormData();
        formDataUpload.append('files', newAvatarFile.file);

        const uploadResponse = await api.post('/upload', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          avatarUrl = uploadResponse.data.data.files[0].url;
        }
      }

      // Mapear roles simplificados para os roles do backend
      const backendRole = userData.role === 'admin' ? 'admin' : 'user';
      
      const response = await userService.createUser({
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role: backendRole
      });
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        navigate('/users');
      },
      onError: (error: any) => {
        console.error('Erro ao criar usuário:', error);
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      alert('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 8) {
      alert('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    createUserMutation.mutate({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      avatar: formData.avatar
    });
  };

    return (
    <FormPage
      backTo="/users"
      backLabel="Voltar aos usuários"
      breadcrumb="Sistema / Usuários / Novo"
      title="Novo usuário"
      subtitle="Convite e permissões de acesso"
    >
          <form id="create-user-form" onSubmit={handleSubmit} className="p-8">
            <div className="space-y-8">
              {/* Avatar Upload */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-center">
                  <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Foto de Perfil
                </h3>
                <FileUpload
                  files={formData.avatar}
                  onFilesChange={(files) => setFormData({ ...formData, avatar: files })}
                  maxFiles={1}
                  maxSizePerFile={5}
                  acceptedTypes={['image/*']}
                  showCategories={false}
                />
                <p className="text-sm text-gray-500 mt-2">Adicione uma foto de perfil (opcional)</p>
              </div>

              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Função *
                </label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'user' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  required
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Administradores podem excluir registros, usuários não podem.
                </p>
              </div>

              {/* Password */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Senha de Acesso
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Senha *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Mínimo de 8 caracteres
                    </p>
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Senha *
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/users')}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createUserMutation.isLoading}
                  className="btn btn-primary"
                >
                  {createUserMutation.isLoading ? 'Criando...' : 'Criar Usuário'}
                </button>
              </div>
            </div>
          </form>
            </FormPage>
  );
};

export default CreateUser;